import { apiClient } from "@/lib/api";
import {
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from "@/components/tickets/config";
import type {
  ConversationMessageChannel,
  MessageDeliveryStatus,
  PaginationParams,
  UserRole,
} from "@/types";

// Re-export enums for convenience
export { TicketStatus, TicketPriority, TicketCategory };
export type {
  ConversationMessageChannel,
  MessageDeliveryStatus,
} from "@/types";

export type ConversationVisibility = "PARTICIPANTS" | "ADMIN_ONLY";

// ============ Types ============

export interface TicketAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_url: string;
  file_type: "image" | "video" | "document";
  file_size: number;
  mime_type: string | null;
  created_at: string;
}

export interface TicketAttachmentCreate {
  file_name: string;
  storage_path: string;
  file_type: "image" | "video" | "document";
  file_size: number;
  mime_type?: string | null;
}

export interface TicketMessage {
  id: string;
  conversation_id: string;
  sender_user_id: string | null;
  content: string;
  is_from_admin: boolean;
  visibility: ConversationVisibility;
  channel: ConversationMessageChannel;
  delivery_status: MessageDeliveryStatus | null;
  delivered_at: string | null;
  created_at: string;
  attachments: TicketAttachment[];
  sender_email: string | null;
  sender_name: string | null;
}

export interface TicketMessageCreate {
  content: string;
  visibility?: ConversationVisibility;
  attachment_urls?: TicketAttachmentCreate[];
}

export interface SupportTicket {
  id: string;
  conversation_id: string;
  owner_user_id: string;
  ticket_number: string;
  category: TicketCategory;
  priority: TicketPriority;
  subject: string;
  status: TicketStatus;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_role: UserRole | null;
  assigned_admin_email: string | null;
  message_count: number;
  last_message_at: string | null;
}

export interface SupportTicketDetail extends SupportTicket {
  messages: TicketMessage[];
}

export interface SupportTicketCreate {
  category: TicketCategory;
  priority: TicketPriority;
  subject: string;
  content: string;
}

export interface SupportTicketUpdate {
  status?: TicketStatus;
  priority?: TicketPriority;
  assigned_to?: string;
}

export interface TicketListResponse {
  total: number;
  items: SupportTicket[];
}

export interface TicketStats {
  total: number;
  open: number;
  in_progress: number;
  waiting_response: number;
  resolved: number;
  closed: number;
  by_priority: Record<number, number>;
  by_category: Record<string, number>;
}

export interface TicketListParams extends PaginationParams {
  status?: TicketStatus;
  ongoing_only?: boolean;
  priority?: TicketPriority;
  category?: TicketCategory;
  assigned_to?: string;
  search?: string;
}

// ============ Query keys ============

export const supportTicketKeys = {
  all: ["support-tickets"] as const,
  lists: () => [...supportTicketKeys.all, "list"] as const,
  myList: (params?: TicketListParams) =>
    [...supportTicketKeys.lists(), "mine", params ?? {}] as const,
  adminList: (params?: TicketListParams) =>
    [...supportTicketKeys.lists(), "admin", params ?? {}] as const,
  details: () => [...supportTicketKeys.all, "detail"] as const,
  myDetail: (id: string) =>
    [...supportTicketKeys.details(), "mine", id] as const,
  adminDetail: (id: string) =>
    [...supportTicketKeys.details(), "admin", id] as const,
  stats: () => [...supportTicketKeys.all, "stats"] as const,
  myStats: () => [...supportTicketKeys.stats(), "mine"] as const,
  adminStats: () => [...supportTicketKeys.stats(), "admin"] as const,
};

// ============ Seller API ============

export const supportTicketService = {
  createTicket: async (
    data: SupportTicketCreate,
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.post<SupportTicketDetail>(
      "/support-tickets",
      data,
    );
    return response.data;
  },

  getMyTickets: async (
    params?: TicketListParams,
  ): Promise<TicketListResponse> => {
    const response = await apiClient.get<TicketListResponse>(
      "/support-tickets/my-tickets",
      {
        params,
      },
    );
    return response.data;
  },

  getMyTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
    const response = await apiClient.get<SupportTicketDetail>(
      `/support-tickets/my-tickets/${ticketId}`,
    );
    return response.data;
  },

  addMessageToMyTicket: async (
    ticketId: string,
    data: TicketMessageCreate,
  ): Promise<TicketMessage> => {
    const response = await apiClient.post<TicketMessage>(
      `/support-tickets/my-tickets/${ticketId}/messages`,
      data,
    );
    return response.data;
  },

  attachToOpeningMessage: async (
    ticketId: string,
    attachment_urls: TicketAttachmentCreate[],
  ): Promise<SupportTicketDetail> => {
    const response = await apiClient.post<SupportTicketDetail>(
      `/support-tickets/my-tickets/${ticketId}/opening-attachments`,
      { attachment_urls },
    );
    return response.data;
  },

  getMyStats: async (): Promise<TicketStats> => {
    const response = await apiClient.get<TicketStats>(
      "/support-tickets/my-stats",
    );
    return response.data;
  },

  getAllTickets: async (
    params?: TicketListParams,
  ): Promise<TicketListResponse> => {
    const response = await apiClient.get<TicketListResponse>(
      "/support-tickets",
      { params },
    );
    return response.data;
  },

  getStats: async (): Promise<TicketStats> => {
    const response = await apiClient.get<TicketStats>("/support-tickets/stats");
    return response.data;
  },

  getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
    const response = await apiClient.get<SupportTicketDetail>(
      `/support-tickets/${ticketId}`,
    );
    return response.data;
  },

  updateTicket: async (
    ticketId: string,
    data: SupportTicketUpdate,
  ): Promise<SupportTicket> => {
    const response = await apiClient.patch<SupportTicket>(
      `/support-tickets/${ticketId}`,
      data,
    );
    return response.data;
  },

  addMessage: async (
    ticketId: string,
    data: TicketMessageCreate,
  ): Promise<TicketMessage> => {
    const response = await apiClient.post<TicketMessage>(
      `/support-tickets/${ticketId}/messages`,
      data,
    );
    return response.data;
  },
};
